import React from 'react';
import {
    Modal,
    Box,
    Typography,
    Button,
    Stack,
    CircularProgress
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import CancelIcon from '@mui/icons-material/Cancel';
import { PartnerListItem } from '../../data/partnerData';
import { deletePartner } from '../../services/partnerInformation';

interface DeletePartnerModalProps {
    isOpen: boolean;
    onClose: () => void;
    partner: PartnerListItem | null;
    refreshPartners: () => Promise<void>;
    setSnackbarDeleteOpen: () => void;
    setDrawerOpen: (open: boolean) => void;
    setSelectedRow: (partner: PartnerListItem | null) => void;
}

const DeletePartnerModal: React.FC<DeletePartnerModalProps> = ({
       isOpen,
       onClose,
       partner,
       refreshPartners,
       setSnackbarDeleteOpen,
       setDrawerOpen,
       setSelectedRow
   }) => {
    const [isDeleting, setIsDeleting] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);

    const handleDelete = async () => {
        if (!partner) return;

        setIsDeleting(true);
        setError(null);

        try {
            await deletePartner(partner.PartnerId);

            onClose();
            setDrawerOpen(false);
            await refreshPartners();
            setSnackbarDeleteOpen();
            setSelectedRow(null);
        } catch (error: any) {
            console.error("Error deleting partner:", error);
            let errorMessage = "Failed to delete partner";

            if (error.message.includes("Unauthorized")) {
                errorMessage = "You are not authorized to delete partners";
            } else if (error.message.includes("not found")) {
                errorMessage = "Partner not found";
            }

            setError(errorMessage);
        } finally {
            setIsDeleting(false);
        }
    };

    const modalStyle = {
        position: 'absolute' as 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: 400,
        bgcolor: 'background.paper',
        boxShadow: 24,
        p: 4,
        borderRadius: 2,
    };

    return (
        <Modal
            open={isOpen}
            onClose={isDeleting ? undefined : onClose}
            aria-labelledby="delete-partner-modal-title"
        >
            <Box sx={modalStyle}>
                <Typography id="delete-partner-modal-title" variant="h6" component="h2" gutterBottom>
                    Delete Partner
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                    Are you sure you want to delete {partner?.Name}? This action cannot be undone.
                </Typography>

                {error && (
                    <Typography color="error" sx={{ mb: 2 }}>
                        {error}
                    </Typography>
                )}

                <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button
                        variant="outlined"
                        startIcon={<CancelIcon />}
                        onClick={onClose}
                        disabled={isDeleting}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        className="delete"
                        startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
                        onClick={handleDelete}
                        disabled={isDeleting}
                    >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </Button>
                </Stack>
            </Box>
        </Modal>
    );
};

export default DeletePartnerModal;